import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	CreateNewColorPack,
	SaveColor,
} from '../../../../../../connection/colors';
import {
	addNewColorPack,
	saveColorPack,
} from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { GetWorkingColorPack } from '../../../../../../store/utils/create-event';
import { Color, CreateColorPack } from '../../../../../../types/working-model';
import TextInput from '../../../../../general-ui/text-input/text';
import Modal from '../../../../../general-ui/modal/modal';
import ColorEditor from './color-editor';

interface Props {
	color: Color;
	selected: boolean;
	onClick: (color: Color) => void;
}

export default function ColorPack(props: Props): JSX.Element {
	const { color, onClick, selected } = props;

	const {
		AuthReducer: { token },
	} = useSelector((state: EventsState) => state);

	const [editingColor, setEditingColor] = useState(false);

	const openEditModal = useCallback(() => {
		setEditingColor(true);
	}, []);

	const closeEditModal = useCallback(() => {
		setEditingColor(false);
	}, []);

	return (
		<div
			className={classNames('color-container', { selected })}
			onClick={() => onClick(color)}
		>
			<div className={'color-active'}>
				<div className="radio">
					<span />
				</div>
			</div>
			<div className="color">
				<div className="heading-row">
					<h6>{color.title}</h6>
					<span onClick={openEditModal}>Edit</span>
				</div>
				<div className="palette-row">
					<div
						style={{
							background: `${color.colors.backgroundColor[0]}`,
							opacity: color.colors.backgroundColor[1],
						}}
					/>
					<div
						style={{
							background: `${color.colors.secondaryBackgroundColor[0]}`,
							opacity: color.colors.secondaryBackgroundColor[1],
						}}
					/>
					<div
						style={{
							background: `${color.colors.bodyTextColor[0]}`,
							opacity: color.colors.bodyTextColor[1],
						}}
					/>
					<div
						style={{
							background: `${color.colors.accentColor[0]}`,
							opacity: color.colors.accentColor[1],
						}}
					/>
					<div
						style={{
							background: `${color.colors.secondaryAccentColor[0]}`,
							opacity: color.colors.secondaryAccentColor[1]
						}}
					/>
					<div
						style={{
							background: `${color.colors.headingTextColor[0]}`,
							opacity: color.colors.headingTextColor[1],
						}}
					/>
				</div>
			</div>
			<ColorEditModal
				modalTitle="Edit color palette"
				isOpen={editingColor}
				onClose={closeEditModal}
				token={token!}
				color={color}
			/>
		</div>
	);
}

interface ColorEditModalProps {
	modalTitle: string;
	onClose: () => void;
	isOpen: boolean;
	token: string;
	color: Color | CreateColorPack;
}
export const ColorEditModal: React.FC<ColorEditModalProps> = ({
	modalTitle,
	onClose,
	isOpen,
	token,
	color,
}) => {
	const [workingColor, setWorkingColor] = useState<
		CreateColorPack | Color | null
	>(null);

	const isEditing = 'color_pack' in color ? true : false;
	const dispatch = useDispatch();

	useEffect(() => {
		if (isOpen) {
			if (Number(color.channel) === 0) {
				setWorkingColor(GetWorkingColorPack(color));
			} else {
				setWorkingColor(color);
			}
		}
	}, [isOpen, color]);

	async function doneCreatingColor() {
		if (workingColor) {
			if (workingColor.color_pack) {
				await SaveColor(token!, workingColor as Color);
				dispatch(saveColorPack(workingColor as Color));
			} else {
				const newColor = await CreateNewColorPack(token!, workingColor);
				dispatch(addNewColorPack(newColor));
			}
		}
		onClose();
	}

	function setPaletteName(e: React.ChangeEvent<HTMLInputElement>) {
		const val = e.target.value;
		setWorkingColor((workingColor: CreateColorPack | Color | null) => {
			if (workingColor) {
				return { ...workingColor, title: val };
			} else {
				return null;
			}
		});
	}

	function setColor(name: keyof Color['colors']) {
		return (hex: string, opacity: number) => {
			setWorkingColor((workingColor: CreateColorPack | Color | null) => {
				if (workingColor) {
					const _workingColor = { ...workingColor };
					_workingColor.colors[name] = [hex, opacity];
					return _workingColor;
				} else {
					return null;
				}
			});
		};
	}
	return (
		<Modal
			closeable={false}
			cancellable={true}
			open={isOpen}
			onRequestClose={onClose}
			title={modalTitle}
			footer={
				<>
					<button onClick={onClose} style={{ marginRight: 16 }}>
						Cancel
					</button>
					<button onClick={doneCreatingColor} className="primary">
						{isEditing ? 'Save' : 'Add'}
					</button>
				</>
			}
		>
			<>
				<TextInput
					label={'New palette name*'}
					placeholder={'Palette Name'}
					onChange={setPaletteName}
					defaultValue={workingColor?.title}
				/>
				<div className="color-selectors">
					{!!workingColor && (
						<>
							<ColorEditor
								color={workingColor!.colors.backgroundColor}
								title={'Background Color'}
								onChange={setColor('backgroundColor')}
							/>
							<ColorEditor
								color={
									workingColor!.colors
										.secondaryBackgroundColor
								}
								title={'Secondary Background Color'}
								onChange={setColor('secondaryBackgroundColor')}
							/>
							<ColorEditor
								color={workingColor!.colors.bodyTextColor}
								title={'Body Text Color'}
								onChange={setColor('bodyTextColor')}
							/>
							<ColorEditor
								color={workingColor!.colors.accentColor}
								title={'Accent Color'}
								onChange={setColor('accentColor')}
							/>
							<ColorEditor
								color={
									workingColor!.colors.secondaryAccentColor
								}
								title={'Secondary Accent Color'}
								onChange={setColor('secondaryAccentColor')}
							/>
							<ColorEditor
								color={workingColor!.colors.containerColor}
								title={'Container Color'}
								onChange={setColor('containerColor')}
							/>
							<ColorEditor
								color={workingColor!.colors.headingTextColor}
								title={'Heading Text Color'}
								onChange={setColor('headingTextColor')}
							/>
						</>
					)}
				</div>
			</>
		</Modal>
	);
};
